import { Component, ReactNode } from 'react';
import { RootState } from '../redux/store';
import moment from 'moment';
import { AuditLog, AuditLogType } from '../domain/rbac/RbacDomains';
import { initialPagePath } from '../redux/reducers/PageReducer';
import NavigationUtil from '../util/NavigationUtil';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../constant/CollectionNames';
import ComponentService, { UserSocketSession } from '../service/ComponentService';
import AuditService from '../service/AuditService';
import ServiceContainer from '../config/ServiceContainer';
import { ServiceNames } from '../constant/ServiceNames';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import and = query.and;
import neq = query.neq;
import eq = query.eq;
import { FormicaProps, withFormica } from '../helpers/withFormica';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { Socket } from 'socket.io-client';

class WithTracker extends Component<Props> {
  private currentTime: number = moment().valueOf();
  private currentPath: Location = { ...window.location };
  private difference: number = 0;

  private timer: NodeJS.Timeout | undefined;

  private auditLogService: AuditService = ServiceContainer.getInstance().get(ServiceNames.AUDIT);
  private componentService: ComponentService = ServiceContainer.getInstance().get(ServiceNames.COMPONENT);
  private socket: Socket = InstanceContainer.get(Injectables.SOCKET_CLIENT);

  private userSessionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    return this.props.children;
  }

  public componentDidMount() {
    const { mouse, pageChange } = this.props;
    if (pageChange) {
      this.initPageChange();
    }
    if (mouse) {
      this.initMouseTracker();
    }
  }

  private initPageChange = () => {
    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());
    this.componentService.pageChange(window.location.href);
  };

  private initMouseTracker = () => {
    document.addEventListener('mousemove', (e: MouseEvent) => this.debounceMove(e));
    this.startTracker();
  };

  private terminatePageChange = () => {
    document.removeEventListener('visibilitychange', () => this.handleVisibilityChange());
  };

  private terminateMouseTrack = async () => {
    document.removeEventListener('mousemove', (e: MouseEvent) => this.debounceMove(e));
    await this.stopTracker();
  };

  private handleVisibilityChange = () => {
    if (document.visibilityState == 'visible') {
      this.currentTime += moment().valueOf() - this.difference;
      this.difference = 0;
    } else {
      this.difference = moment().valueOf();
    }
  };

  private startTracker = () => {
    const _query = query.QueryBuilder.select()
      .from(CollectionNames.USER_SESSIONS)
      .where(and(eq('url', window.location.href), neq('id', this.socket.id)))
      .build();
    this.userSessionSubscription = subscribe.Formica.subscribe(CollectionNames.USER_SESSIONS, _query);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.userSessionSubscription]);
  };

  private tracker = async () => {
    if (this.userSessionSubscription?.ready()) {
      const _query = query.QueryBuilder.select()
        .from(CollectionNames.USER_SESSIONS)
        .where(and(eq('url', window.location.href), neq('id', this.socket.id)))
        .build();
      const sessions = await this.repository.findMany<UserSocketSession>(_query);
      this.drawUsers(sessions);
    }
  };

  private drawUsers = (sessions: UserSocketSession[]) => {
    const userTracker = document.getElementById('user-tracker');
    if (userTracker) {
      userTracker.remove();
    }
    const div = document.createElement('div');
    div.id = 'user-tracker';
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left = '0';
    div.style.width = '100%';
    div.style.height = '100%';
    div.style.pointerEvents = 'none';
    div.style.zIndex = '1001';

    sessions
      .filter((s) => s.view != undefined)
      .forEach((s) => {
        const span = document.createElement('span');
        span.style.position = 'absolute';
        span.style.top = `${s.view!.y}px`;
        span.style.left = `${s.view!.x}px`;
        span.style.width = '10px';
        span.style.height = '10px';
        span.style.borderRadius = '50%';
        span.style.backgroundColor = 'red';
        span.style.pointerEvents = 'none';
        div.appendChild(span);
      });

    document.body.appendChild(div);
  };

  private stopTracker = async () => {
    await this.userSessionSubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    const { mouse } = this.props;
    if (!mouse) return;
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  public componentDidUpdate(prevProps: Props) {
    if (prevProps.router.location != this.props.router.location) {
      this.handlePageChange();
    }
    if (prevProps.mouse != this.props.mouse) {
      if (this.props.mouse) {
        this.initMouseTracker();
      } else {
        this.terminateMouseTrack();
      }
    }

    if (this.props.pageChange != prevProps.pageChange) {
      if (this.props.pageChange) {
        this.initPageChange();
      } else {
        this.terminatePageChange();
      }
    }
  }

  private debounceMove = (e: MouseEvent) => {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = setTimeout(() => {
      this.componentService.mouseMove(e);
    }, 100);
  };

  private handlePageChange = async () => {
    const { user, pageChange } = this.props;
    if (!pageChange) return;
    const location = this.currentPath ?? window.location;
    const timestamp = moment().valueOf();
    const auditLog: AuditLog = {
      username: user.username,
      operation: AuditLogType.PAGE_VIEW,
      document: {
        id: location.pathname,
        data: JSON.stringify({
          path: location.pathname,
          query: location.search,
          time: timestamp - this.currentTime,
        }),
      },
      ip: '0',
      timestamp: moment().valueOf(),
      component:
        Object.entries(initialPagePath).find((v) => location.pathname.startsWith(`/${NavigationUtil.getTenant()}/${v[1].path}`))?.[0] ||
        location.pathname.split('/')[2] ||
        (location.pathname as any),
    };
    await this.auditLogService.save(auditLog);
    this.currentPath = { ...window.location };
    this.currentTime = timestamp;

    this.componentService.pageChange(window.location.href);
    await this.resubscribeTracker();
  };
}

interface Props extends ReduxProps, FormicaProps {
  mouse: boolean;
  pageChange: boolean;
  children: ReactNode;
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  component: state.component,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(WithTracker));
